import {
  allEnquiries, allInvoices, allProjects, listInstructions, getProjectInvoices,
  notesForProject, listSiteVisits, asDate, askRecords, projectEmails,
} from '../db.js';
import { threadFor } from '../golden-thread.js';
import { openGoldenThread } from './golden-thread-panel.js';

// LOADING THE THREAD (Track Golden Thread.html, turn 2 "four ways in").
//
// One loader behind all four entry points, because the number on the project
// header, the number on the status card, the number in the client list's Thread
// column and the panel itself have to be the SAME number. Four call sites each
// counting for themselves is exactly how the old panel came to disagree with
// the card above it.
//
// Money records are read only when the viewer may see figures. A viewer who may
// not still gets the invoice ON the thread, withheld — see rule 3e: hidden must
// not look broken. Reading them and hiding them in the browser would put the
// figures in the page; not reading them at all would break the line. So the
// FETCH is gated and a placeholder record carries the node.

const dateOf = (v) => { const d = asDate(v); return d ? d.toISOString() : null; };

/** A meeting/call record from a note or a site visit — the fourth record type. */
const noteFrom = (n) => ({
  id: n.id, at: dateOf(n.createdAt || n.at), title: n.text || n.title,
  byName: n.userName || n.byName || null, byId: n.userEmail || null, kind: n.addedFrom || null,
});
const visitAsNote = (v) => ({
  id: `visit:${v.id}`, at: dateOf(v.startedAt),
  title: v.purpose ? `Site visit — ${v.purpose}` : 'Site visit',
  byName: v.engineerName || null, byId: v.engineerEmail || null, kind: 'site visit',
  detail: v.notes || '', evidence: (v.items || []).slice(0, 6).map((i) => i.caption || i.kind || 'photo'),
});

/**
 * The mapping, from records the caller ALREADY HAS.
 *
 * Split out from the loader because the project page has all of this in hand by
 * the time it paints, and a second round of reads to draw the same number on
 * the header is how the header and the panel end up disagreeing.
 */
export function sourceFromLoaded({ project = {}, instructions = [], invoices = [], notes = [], visits = [], enquiry = null, isMoney = true }) {
  const enq = enquiry ? [enquiry] : [];
  return {
    enquiries: enq.map((e) => ({
      id: e.id, number: e.number, receivedAt: dateOf(e.receivedAt || e.createdAt),
      clientName: e.clientName, status: e.status, quotedFee: isMoney ? e.quotedFee || e.proposedFee : null,
      byName: e.loggedByName || null, subject: e.subject || e.description,
    })),
    instructions: instructions.map((i) => ({
      id: i.id, seq: i.seq, instructedAt: dateOf(i.instructedAt || i.createdAt || project.createdAt),
      description: i.description, status: i.status, by: i.raisedByName || null,
      byName: i.raisedByName || null, fee: isMoney ? i.fee : null,
    })),
    invoices: invoices.map((v) => ({
      id: v.id, number: v.number, invoiceDate: dateOf(v.invoiceDate || v.createdAt),
      amount: v.amount, paymentStatus: v.paymentStatus, paidDate: v.paidDate,
    })),
    notes: [...notes.map(noteFrom), ...visits.map(visitAsNote)].filter((n) => n.at),
    // The shield, expressed as access rather than as absence.
    canSee: (r) => isMoney || !('amount' in r || 'invoiceDate' in r),
  };
}

/**
 * Every record on one project's thread, loaded.
 *
 * @param opts.isMoney whether this viewer may see figures (the shield)
 */
export async function projectThreadSource(project, { isMoney = true } = {}) {
  const id = project.id;
  const no = String(project.number || '');
  const [instructions, invoices, notes, visits, enquiries] = await Promise.all([
    listInstructions(id).catch(() => []),
    isMoney ? getProjectInvoices(id).catch(() => []) : Promise.resolve([]),
    no ? notesForProject(no).catch(() => []) : Promise.resolve([]),
    listSiteVisits(id).catch(() => []),
    allEnquiries().catch(() => []),
  ]);
  const instr1 = instructions.find((i) => Number(i.seq) === 1);
  const enqNo = project.enquiryNumber
    || (instr1 && instr1.enquiryRef ? String(instr1.enquiryRef).split('/').pop() : null);
  const enquiry = enqNo ? enquiries.find((e) => e.id === enqNo || e.number === enqNo) || null : null;
  return sourceFromLoaded({ project, instructions, invoices, notes, visits, enquiry, isMoney });
}

/** Every record on a CLIENT's thread — the wider read of the same line. */
export async function clientThreadSource(client, { isMoney = true } = {}) {
  const cref = `clients/${client.id}`;
  const matches = (r) => r.clientRef === cref
    || (r.clientName && client.name && String(r.clientName).toLowerCase() === String(client.name).toLowerCase());
  const [projects, enquiries, invoices] = await Promise.all([
    allProjects().catch(() => []),
    allEnquiries().catch(() => []),
    isMoney ? allInvoices().catch(() => []) : Promise.resolve([]),
  ]);
  const mine = projects.filter(matches);
  const perProject = await Promise.all(mine.slice(0, 12).map((p) => Promise.all([
    listInstructions(p.id).catch(() => []),
    notesForProject(String(p.number || '')).catch(() => []),
  ]).then(([ins, notes]) => ({ p, ins, notes }))));

  return {
    enquiries: enquiries.filter(matches).map((e) => ({
      id: e.id, number: e.number, receivedAt: dateOf(e.receivedAt || e.createdAt),
      clientName: e.clientName, status: e.status, quotedFee: isMoney ? e.quotedFee : null,
      subject: e.subject || e.description,
    })),
    instructions: perProject.flatMap(({ p, ins }) => ins.map((i) => ({
      id: `${p.id}:${i.id}`, seq: i.seq, instructedAt: dateOf(i.instructedAt || i.createdAt || p.createdAt),
      description: `${p.number} — ${i.description || 'Instruction'}`, status: i.status,
      byName: i.raisedByName || null, fee: isMoney ? i.fee : null,
    }))),
    invoices: invoices.filter(matches).map((v) => ({
      id: v.id, number: v.number, invoiceDate: dateOf(v.invoiceDate || v.createdAt),
      amount: v.amount, paymentStatus: v.paymentStatus, paidDate: v.paidDate,
    })),
    notes: perProject.flatMap(({ notes }) => notes.map(noteFrom)).filter((n) => n.at),
    canSee: (r) => isMoney || !('amount' in r || 'invoiceDate' in r),
    projects: mine,
  };
}

/**
 * The number the four entry points show without opening anything. Cheap enough
 * to run on a list: it reuses whatever the caller already loaded.
 */
export function threadHealth(src, { hasFee = false, now = Date.now() } = {}) {
  const t = threadFor(src, { now, hasFee });
  return { done: t.score.done, total: t.score.total, breaks: t.breaks.length, healthy: t.healthy, thread: t };
}

/**
 * The thread, as evidence for a question about it.
 *
 * Only what a record SAYS goes over: no ids beyond the one the answer must cite
 * back, no figures a withheld record was carrying. A withheld node crosses as
 * the fact that it exists and nothing else, which is what the reader is allowed
 * to know and therefore all the answer may ever say about it.
 */
function asEvidence(thread) {
  return {
    nodes: thread.nodes.map((n) => (n.withheld
      ? { id: n.id, kind: n.kind, at: n.at, withheld: true }
      : {
        id: n.id, kind: n.kind, at: n.at, eyebrow: n.eyebrow, title: n.title,
        meta: n.meta, ref: n.ref, byName: n.byName, chips: n.chips,
        detail: (n.record && (n.record.detail || n.record.notes)) || null,
      })),
    breaks: thread.breaks.map((b) => ({ key: b.key, title: b.title, why: b.why, days: b.days, ownerLine: b.ownerLine })),
  };
}

/**
 * Ask the records the question, and fill the answer into the open panel.
 *
 * Never blocks the thread: the panel is already up and readable, and this
 * arrives into it. A failure here leaves the thread exactly as it was before
 * the engine existed — the question pinned, nothing claimed over it.
 */
/**
 * The job's emails as evidence (Karan 06/10/2026). Under the project number and
 * the enquiry number it was won from — mail tagged TRACK// A0209 stays indexed
 * under the A-number (db.js projectEmails). Newest 30, trimmed; a failure here
 * just means the answer comes from the thread alone.
 */
async function emailEvidence(project) {
  if (!project || !project.number) return [];
  try {
    const enq = (await allEnquiries().catch(() => []))
      .find((e) => String(e.projectNo || '') === String(project.number) || (e.projectId && e.projectId === project.id));
    const rows = await projectEmails(project.number, 30, enq && enq.number);
    return rows.map((e) => ({
      id: e.id, threadId: e.threadId || e.id, at: dateOf(e.date), direction: e.direction || null,
      from: e.from || '', to: Array.isArray(e.to) ? e.to.join(', ') : (e.to || ''),
      subject: e.subject || '', snippet: e.snippet || '',
      body: String(e.body || '').slice(0, 700), permalink: e.permalink || null,
    }));
  } catch (e) { return []; }
}

async function answerFromRecords({ panel, answer, subject, project = null, extraRecords = null }) {
  try {
    const emails = await emailEvidence(project);
    const thread = asEvidence(panel().thread);
    // Records the page holds that the thread does not draw (the site
    // constraints snapshot, brief §9): node-shaped, read-only, appended as
    // evidence only — the panel itself is not changed by them.
    const extra = typeof extraRecords === 'function' ? (extraRecords() || []) : [];
    if (extra.length) thread.nodes = [...thread.nodes, ...extra];
    const res = await askRecords({ question: answer.question, subject, thread, emails });
    if (res && res.status === 'answered' && res.answer) {
      answer.answer = res.answer;
      answer.openAt = res.openAt || null;
      answer.email = res.email || null;
    } else {
      answer.gap = (res && (res.gap || (res.status === 'limited' ? res.answer : null)))
        || 'Track holds no record that answers that.';
    }
  } catch (e) {
    console.warn('[gt] records answer —', e.message);
    answer.gap = 'Track could not reach the record engine just now.';
  }
  answer.pending = false;
  // `panel()` is read again here, not captured: switching scope while the
  // question is in flight closes the first panel and mounts another, and the
  // answer must land in the one the reader is looking at.
  panel().setAnswer(answer);
}

/**
 * Open the panel on a project, with the client scope one click away.
 * The panel itself never reads or writes — the caller owns every side effect.
 */
export async function openProjectThread({ project, client, session, isMoney = true, answer = null, source = null, extraRecords = null, on = {} }) {
  let scope = 'project';
  const cache = source ? { project: source } : {};

  const load = async (which) => {
    if (cache[which]) return cache[which];
    cache[which] = which === 'client' && client
      ? await clientThreadSource(client, { isMoney })
      : await projectThreadSource(project, { isMoney });
    return cache[which];
  };

  // Pending BEFORE the first paint, so the answer line never flashes the
  // "here is the whole thread" copy on its way to saying it is reading.
  if (answer && answer.question && !answer.answer) answer.pending = true;

  let handle = null;
  const show = async (which) => {
    scope = which;
    const src = await load(which);
    const isClient = which === 'client';
    if (handle) handle.close();
    handle = openGoldenThread({
      title: isClient ? (client.name || 'Client')
        : `${project.number || ''}${project.title ? ` · ${project.title}` : ''}`.trim(),
      subtitle: isClient
        ? 'Every record for this client, in one line.'
        : `Every record for this project — the enquiry it came from, what was instructed, decided, invoiced and said.`,
      scopes: [
        { key: 'project', label: `Project ${project.number || ''}`.trim(), active: !isClient },
        { key: 'client', label: client ? (client.name || 'Client') : 'No client record', active: isClient, disabled: !client },
      ],
      records: src, session, answer, openAt: answer && answer.openAt,
      hasFee: !!(Number(project.fee) > 0),
      fee: isMoney && project.fee ? `£${Number(project.fee).toLocaleString('en-GB')}` : null,
      on: { ...on, scope: (k) => { if (k !== scope) show(k); } },
    });
  };
  await show(client && !project ? 'client' : 'project');
  // Asked ONCE, here, outside show(): switching to the client thread and back
  // re-mounts the panel and must not re-ask the question or re-spend on it.
  // The answer lives on the `answer` object, so every re-mount already has it.
  if (answer && answer.pending) {
    answerFromRecords({ panel: () => handle, answer, project, extraRecords, subject: `${project.number || ''} ${project.title || ''}`.trim() });
  }
  return { close: () => handle && handle.close() };
}

/** Open the panel on a client with no project in hand (the client list). */
export async function openClientThread({ client, session, isMoney = true, on = {} }) {
  const src = await clientThreadSource(client, { isMoney });
  return openGoldenThread({
    title: client.name || 'Client',
    subtitle: 'Every record for this client, in one line.',
    scopes: [{ key: 'client', label: client.name || 'Client', active: true }],
    records: src, session, on,
    hasFee: src.enquiries.some((e) => Number(e.quotedFee) > 0),
  });
}
